import {
  isValidPhoneNumber,
  validatePhoneNumberLength,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import { countries } from "countries-list";

/**
 * Single source of truth for phone-number validation across the whole admin panel
 * (Settings, Admin create/view, Instructor, Student). Backed by libphonenumber-js so
 * the rules are accurate per country (exact lengths + valid prefixes), not a loose
 * "6–15 digits" regex.
 *
 * Two call shapes are supported, matching how the different forms store phone numbers:
 *  1. national digits + a country NAME (Settings / Admin screens), e.g. ("9876543210", "India")
 *  2. a combined international string (PhoneNumberField), e.g. ("+91 9876543210") — country optional
 */

// Generous cap for native input maxLength. E.164 allows at most 15 digits total, so this
// never blocks a valid number; correctness is enforced by getPhoneValidationMessage.
export const PHONE_INPUT_MAX_DIGITS = 15;

const GENERIC_MIN_DIGITS = 6;

// countries-list is keyed by ISO-3166 alpha-2 (the codes libphonenumber-js expects).
const NAME_TO_ISO2: Record<string, CountryCode> = Object.entries(countries).reduce(
  (acc, [iso2, country]) => {
    acc[(country as { name: string }).name] = iso2 as CountryCode;
    return acc;
  },
  {} as Record<string, CountryCode>,
);

export const countryNameToIso2 = (
  countryName?: string | null,
): CountryCode | undefined =>
  countryName ? NAME_TO_ISO2[countryName] : undefined;

/**
 * Returns "" when the number is valid (or empty — callers handle "required" themselves),
 * otherwise a human-readable error message.
 */
export const getPhoneValidationMessage = (
  value: string,
  countryName?: string | null,
): string => {
  const raw = (value || "").trim();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return ""; // empty is treated as "not yet entered"; required-ness is per-form

  const hasInternationalPrefix = raw.startsWith("+");
  const iso2 = countryNameToIso2(countryName);

  // No "+" prefix and no resolvable country → fall back to a loose minimum-length check
  // so we never block input when we genuinely can't determine the country.
  if (!hasInternationalPrefix && !iso2) {
    if (digits.length < GENERIC_MIN_DIGITS || digits.length > PHONE_INPUT_MAX_DIGITS) {
      return `Phone number must be ${GENERIC_MIN_DIGITS} to ${PHONE_INPUT_MAX_DIGITS} digits`;
    }
    return "";
  }

  const input = hasInternationalPrefix ? raw : digits;
  const defaultCountry = hasInternationalPrefix ? undefined : iso2;

  if (isValidPhoneNumber(input, defaultCountry)) return "";

  const suffix = countryName ? ` for ${countryName}` : "";
  switch (validatePhoneNumberLength(input, defaultCountry)) {
    case "TOO_SHORT":
      return `Phone number is too short${suffix}`;
    case "TOO_LONG":
      return `Phone number is too long${suffix}`;
    default:
      return `Please enter a valid phone number${suffix}`;
  }
};

export const isValidPhone = (
  value: string,
  countryName?: string | null,
): boolean => getPhoneValidationMessage(value, countryName) === "";

/**
 * Extracts the national (subscriber) digits from a stored value for display in a field
 * that shows the dial code separately. Robust across the formats used in the app:
 * "+91 9876543210", "919876543210", or a bare national "9876543210". Falls back to the
 * raw digits when the value can't be parsed, so a number is never blanked out.
 */
export const getNationalNumber = (
  value: string,
  countryName?: string | null,
): string => {
  const raw = (value || "").trim();
  if (!raw) return "";

  const iso2 = countryNameToIso2(countryName);
  const parsed = raw.startsWith("+")
    ? parsePhoneNumberFromString(raw)
    : parsePhoneNumberFromString(raw, iso2);

  if (parsed?.nationalNumber) return String(parsed.nationalNumber);
  return raw.replace(/\D/g, "");
};
